import { useState } from 'react';
import { PhoneOverride } from '@weave/schema-gen-ts/dist/schemas/phone/override/v1/override.pb';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { PhoneOverrideType } from 'libs/features/phone/src/components/override/types';
import { LocationsApi } from '@frontend/api-locations';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocalizedQuery } from '@frontend/location-helpers';
import {
  queryKeys,
  NewOverrideSettingRemoveModal,
  NewOverrideSettingTray,
  useOverridesManagementPermission,
} from '@frontend/phone';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { usePhoneOverrideShallowStoreV2 } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  Button,
  Chip,
  Modal,
  PopoverMenu,
  PopoverMenuItem,
  SecondaryButton,
  Table,
  Text,
  TextLink,
  useAlert,
  useModalControl,
  usePopoverMenu,
} from '@frontend/design-system';

dayjs.extend(timezone);

export const OverrideNotificationRibbon = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const alerts = useAlert();
  const { overridesV2 } = usePhoneOverrideShallowStoreV2('overridesV2');
  const phoneOverrides = overridesV2.phoneOverrides;
  const enabledOverridesV2 = phoneOverrides?.filter((override) => override.enabled) || [];
  const { getLocationName, accessibleLocationData, selectedLocationIds, groupData } = useAppScopeStore();
  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations: selectedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [],
  });

  const filteredEnabledOverridesV2 = enabledOverridesV2.filter((item) => selectedLocationIds.includes(item.locationId));
  const hasOverridesManagementPermission = useOverridesManagementPermission();
  const hasNewUsersManagementFlag = filteredEnabledOverridesV2.every((override) =>
    hasOverridesManagementPermission(override?.locationId ?? '')
  );
  const locationData = accessibleLocationData[selectedLocationIds[0]];
  const selectedLocationData = locationData;

  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({ placement: 'left' });
  const { navigate } = useSettingsNavigate();
  const { modalProps, openModal, closeModal } = useModalControl();
  const removeOverride = useModalControl();
  const tray = useModalControl();
  const { setSettingsTenantLocation } = usePhoneSettingsShallowStore('setSettingsTenantLocation');
  const hasSingleEnabledOverride = filteredEnabledOverridesV2.length === 1;
  const [overrideV2, setOverrideV2] = useState<Partial<PhoneOverrideType>>({});

  const getCallRouteName = () => {
    if (filteredEnabledOverridesV2?.length === 1) {
      return t('{{callRouteName}}', { callRouteName: filteredEnabledOverridesV2[0].callRouteName });
    } else if (filteredEnabledOverridesV2?.length > 1) {
      return t('{{count}} Call Routes', { count: filteredEnabledOverridesV2.length });
    }
    return null;
  };

  const OverrideLinkV2 = () => {
    return phoneAccess.hasFullPhoneAccess ? (
      <TextLink
        onClick={() =>
          navigate({
            to: '/phone/call-routes/:id',
            params: { id: filteredEnabledOverridesV2[0].callRouteId ?? '' },
          })
        }
      >
        {getCallRouteName()}
      </TextLink>
    ) : (
      <Text size='medium' as='span'>
        {getCallRouteName()}
      </Text>
    );
  };

  const message2 = hasSingleEnabledOverride ? (
    <Trans t={t}>
      Call Route <OverrideLinkV2 /> has an active override and will not direct calls normally until the override is
      removed.
    </Trans>
  ) : (
    <Trans t={t}>
      {getCallRouteName()} have active overrides and will not direct calls normally until the overrides are removed.
    </Trans>
  );

  const actionButton = (
    <TextLink
      weight='bold'
      onClick={() => {
        if (selectedLocationData && hasSingleEnabledOverride) {
          setOverrideV2(filteredEnabledOverridesV2[0]);
          tray.openModal();
        } else if (selectedLocationData) {
          const group =
            selectedLocationData.locationType === 'databeta'
              ? { ...selectedLocationData, children: [] }
              : groupData[selectedLocationData.groupId];
          setSettingsTenantLocation({
            id: group.id,
            phoneTenantId: group.phoneTenantId ?? '',
            locationId: group.id,
            locationType: group.locationType,
            name: group.name ?? '',
            children: group.children.map((child) => ({
              id: child.id,
              name: child.name ?? '',
              locationType: child.locationType,
              phoneTenantId: child.phoneTenantId ?? '',
              children: [],
              locationId: child.id,
              orgId: child.orgId,
              type: 'individual' as const,
            })),
            orgId: group.orgId,
            type: group.children.length > 0 ? 'group' : 'individual',
          });
          navigate({ to: '/phone/overrides' });
          closeModal();
        }
      }}
      trackingId='phone-portal-weave2.0-override-notification-btn-manageInSettings'
    >
      {t('Go to Overrides')}
    </TextLink>
  );

  const onClickHandler = () => {
    if (selectedLocationData) {
      setOverrideV2(filteredEnabledOverridesV2[0]);
      tray.openModal();
    } else {
      alerts.error(t('No selected location data found.'));
    }
  };

  const getOverrideType = (override: PhoneOverride) => {
    const { overrideType } = override;

    switch (overrideType) {
      case 'OVERRIDE_TYPE_VMO':
        return t('Send to Voicemail');
      case 'OVERRIDE_TYPE_FWD':
        return t('Forward Call');
      default:
        return '--';
    }
  };

  const { data: officeTimezone } = useLocalizedQuery({
    queryKey: queryKeys.timezone(selectedLocationIds[0]),
    queryFn: () => LocationsApi.getOfficeTimezone({}),
    select: (data) => data.timezone,
  });

  const getEndTime = (override: PhoneOverride | undefined, officeTimezone: string | undefined) => {
    if (override && override.endAt) {
      return dayjs(override.endAt).tz(officeTimezone).format('MMM D YYYY h:mma z');
    }
    return '--';
  };

  const handleOpenRemoveModalV2 = (overrideV2: PhoneOverrideType) => {
    setOverrideV2(overrideV2);
    removeOverride.openModal();
  };

  const handleCreateV2 = (overrideV2: PhoneOverrideType) => {
    setOverrideV2(overrideV2);
    tray.openModal();
  };

  const [showBanner, setShowBanner] = useState(true);
  const handleDismiss = () => setShowBanner(false);

  return (
    <>
      {showBanner && !!filteredEnabledOverridesV2?.length && (
        <GlobalBannerDynamic
          id='override-notification-banner'
          type='warning'
          title={hasSingleEnabledOverride ? t('Active Override') : t('Active Overrides')}
          message={message2}
          isDismissible
          onDismiss={handleDismiss}
          action={{
            label: hasSingleEnabledOverride
              ? hasNewUsersManagementFlag
                ? t('Edit Override')
                : t('View Override')
              : hasNewUsersManagementFlag
              ? t('Manage Overrides')
              : t('View Overrides'),
            onClick: hasSingleEnabledOverride ? onClickHandler : openModal,
          }}
        />
      )}

      <Modal {...modalProps} minWidth={800}>
        <Modal.Header
          onClose={closeModal}
          closeBtnTrackingId='phone-portal-weave2.0-override-notification-btn-closeModal'
        >
          {t('Call Routes with Active Overrides')}
        </Modal.Header>
        <Modal.Body>
          <Text>{t('Call Routes with active Overrides are unable to receive calls.')}</Text>
          <Table
            colConfig={[
              {
                Header: t('Call Route'),
                accessor: (override) =>
                  phoneAccess.hasFullPhoneAccess ? (
                    <TextLink
                      onClick={() => {
                        closeModal();
                        navigate({
                          to: '/phone/call-routes/:id',
                          params: { id: override.callRouteId ?? '' },
                        });
                      }}
                    >
                      {override.callRouteName}
                    </TextLink>
                  ) : (
                    <Text size='medium'>{override.callRouteName}</Text>
                  ),
                id: 'name',
                width: 100,
              },
              {
                Header: t('Override Type'),
                id: 'override type',
                accessor: (override) => getOverrideType(override),
                width: 100,
              },
              {
                Header: t('Override Ends'),
                id: 'end time',
                accessor: (override) => getEndTime(override, officeTimezone),
                width: 100,
              },
              {
                Header: t('Location'),
                accessor: (override) => (
                  <Chip.Location variant='primary'>{getLocationName(override.locationId ?? '')}</Chip.Location>
                ),
                id: 'location',
                width: 100,
                omit: selectedLocationIds.length === 1,
              },
              {
                Header: '',
                id: 'viewAction',
                disableSortBy: true,
                maxWidth: 10,
                accessor: (data) => data,
                cellRenderer: (row) => {
                  return hasOverridesManagementPermission(row.locationId) ? (
                    <div>
                      <Button
                        iconName='more'
                        label={t('Open Menu')}
                        size='large'
                        variant='secondary'
                        {...getTriggerProps()}
                      />
                      <PopoverMenu {...getMenuProps()}>
                        <PopoverMenuItem
                          {...getItemProps}
                          Icon={() => <Icon name='edit-small' />}
                          onClick={() => {
                            close();
                            handleCreateV2(row);
                          }}
                          trackingId='phone-portal-weave2.0-override-notification-btn-edit'
                        >
                          <Text as='span'>{t('Edit Override')}</Text>
                        </PopoverMenuItem>
                        <PopoverMenuItem
                          {...getItemProps}
                          Icon={() => <Icon name='trash-small' color='error' />}
                          onClick={() => {
                            close();
                            handleOpenRemoveModalV2(row);
                          }}
                          trackingId='phone-portal-weave2.0-override-notification-btn-remove'
                        >
                          <Text as='span'>{t('Remove Override')}</Text>
                        </PopoverMenuItem>
                      </PopoverMenu>
                    </div>
                  ) : (
                    <Button
                      iconName='view'
                      size='large'
                      variant='secondary'
                      onClick={() => {
                        close();
                        handleCreateV2(row);
                      }}
                      trackingId='phone-portal-weave2.0-override-notification-btn-view'
                    />
                  );
                },
              },
            ]}
            hasResponsiveColWidths
            data={filteredEnabledOverridesV2}
          ></Table>
          <div
            css={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(3), marginTop: theme.spacing(3) }}
          >
            {actionButton}
            <SecondaryButton
              onClick={closeModal}
              size='tiny'
              trackingId='phone-portal-weave2.0-override-notification-btn-closeModal'
            >
              {t('Close')}
            </SecondaryButton>
          </div>
        </Modal.Body>
      </Modal>

      <NewOverrideSettingRemoveModal
        override={overrideV2}
        modalProps={removeOverride.modalProps}
        phoneTenantId={selectedLocationData?.phoneTenantId ?? ''}
      />
      <NewOverrideSettingTray
        override={overrideV2}
        locationId={selectedLocationIds[0]}
        modalControls={tray}
        overridesData={overridesV2}
      />
    </>
  );
};
